import {filter, take} from 'rxjs/operators';
import {useEffect} from 'react';

import {Conversation} from '@stripe-internal/conversations-client';
import {ViewMode} from '@stripe-internal/support-conversations-controller';

import {isDotComConversation} from '../../core/lib/util/conversationUtils';
import {conversationLogMessages as messages} from '../../core/messages';
import {useApp, useViewMode} from '../../core/hooks/appHooks';

export {messages};

function getAgentName(conversation: Conversation): string | undefined {
  return Object.values(conversation.participants).find(
    (p) => p.role === 'agent',
  )?.displayName;
}

function getMessageForConversation(
  conversation: Conversation,
  drawerEnabled: boolean,
  withHistory = false,
) {
  const {channel} = conversation.context;
  const agentName = getAgentName(conversation);

  if (channel === 'request_a_call') {
    if (conversation.context.requestACallDetails?.callCanceled) {
      if (withHistory) {
        return {message: messages.racCanceledWithHistory};
      }
      return {
        message: drawerEnabled
          ? messages.racCanceled
          : messages.racCanceledForWidget,
      };
    }
    if (withHistory) {
      return {message: messages.racEndedWithHistory};
    }
    return {
      message: drawerEnabled ? messages.racEnded : messages.racEndedForWidget,
    };
  } else if (agentName && channel === 'chat') {
    return {
      message: withHistory
        ? messages.chatWithAgentEndedWithHistory
        : messages.chatWithAgentEnded,
      values: {name: agentName},
    };
  }
  return {
    message: drawerEnabled
      ? messages.conversationEnded
      : messages.conversationEndedForWidget,
  };
}

export function useConversationLogEventsForViewMode(
  conversation: Conversation,
  viewMode: ViewMode,
  withHistory = false,
) {
  const drawerEnabled = viewMode === ViewMode.Drawer;
  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(
        filter((e) => e.name === 'conversation-ended'),
        take(1),
      )
      .subscribe(() => {
        // For dot_com entry points, let UHLiveChannelsClient handle when to show the chat ended message.
        // It'll show after animations are complete.
        if (isDotComConversation(conversation)) {
          return;
        }
        const {message, values} = getMessageForConversation(
          conversation,
          drawerEnabled,
          withHistory,
        );
        if (!conversation.context.supressChatEndedNotice) {
          conversation.log.appendMetaEvent({
            id: 'chat-ended-notice',
            message,
            values,
          });
        }
      });

    return () => sub.unsubscribe();
  }, [conversation, drawerEnabled, withHistory]);
}

export function useConversationLogEvents(conversation: Conversation) {
  const app = useApp();
  const viewMode = useViewMode();
  useConversationLogEventsForViewMode(conversation, viewMode);

  useEffect(() => {
    const sub = conversation
      .eventsObservable()
      .pipe(filter((event) => event.name === 'conversation-message-added'))
      .subscribe((event) => {
        if (!event.message) {
          return;
        }

        app.analytics.action('universal_help_conversation_message', {
          conversationId: conversation.id,
          stage: 'message_added_to_log',
          participantRole: event.message.participant.role,
          messageId: event.message.id,
          responseId: event.message.responseId,
          messageType: event.message.type,
          nonce: event.nonce,
        });
      });

    return () => sub.unsubscribe();
  }, [app.analytics, conversation]);
}
